import { useMemo, useRef, useState } from 'react';
import {
  UserCircle, Plus, Pencil, Trash2, Eye, Search, Mail, Phone, X, Building2,
} from 'lucide-react';
import {
  Button, MessagePlugin, Dialog, Form, Input, Table, Space, Popconfirm,
} from 'tdesign-react';
import ProductImageUploader from '../../components/ProductImageUploader';
import { tokenStorage } from '@/user/api';
import { EMAIL_PATTERN, PHONE_PATTERN } from '@/utils/contactValidation';

const { FormItem } = Form;

export interface Account {
  id: number;
  avatar?: string;
  email: string;
  nickname: string;
  password: string;
  phone: string;
  tenantId: string;
  username: string;
}

const initialAccounts: Account[] = [
  {
    id: 1,
    username: 'admin_zhang',
    nickname: '张管理员',
    email: 'zhang.admin@example.com',
    phone: '13800138001',
    tenantId: '1001',
    password: '123456',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=zhang',
  },
  {
    id: 2,
    username: 'lisi_ops',
    nickname: '李运营',
    email: 'lisi@example.com',
    phone: '13800138002',
    tenantId: '1001',
    password: '123456',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=lisi',
  },
  {
    id: 3,
    username: 'wangwu_dev',
    nickname: '王开发',
    email: 'wangwu@example.com',
    phone: '13800138003',
    tenantId: '1002',
    password: '123456',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=wangwu',
  },
];

const AVATAR_FALLBACK =
  'https://api.dicebear.com/7.x/initials/svg?seed=account';

function maskPassword(_password: string) {
  return '••••••';
}

export default function AccountManage() {
  const [accounts, setAccounts] = useState<Account[]>(initialAccounts);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [avatarValue, setAvatarValue] = useState('');
  const formInstanceRef = useRef<any>(null);

  const [showDetail, setShowDetail] = useState(false);
  const [detailAccount, setDetailAccount] = useState<Account | null>(null);

  const filteredAccounts = useMemo(() => {
    const kw = searchText.trim().toLowerCase();
    if (!kw) return accounts;
    return accounts.filter(
      (a) =>
        a.username.toLowerCase().includes(kw) ||
        a.nickname.toLowerCase().includes(kw) ||
        a.email.toLowerCase().includes(kw) ||
        a.phone.includes(kw) ||
        a.tenantId.includes(kw),
    );
  }, [accounts, searchText]);

  const pagedData = useMemo(() => {
    const start = (pagination.current - 1) * pagination.pageSize;
    return filteredAccounts.slice(start, start + pagination.pageSize);
  }, [filteredAccounts, pagination]);

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    setSearchText('');
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const resetFormState = () => {
    setEditingAccount(null);
    setAvatarValue('');
    formInstanceRef.current?.reset();
  };

  const openCreate = () => {
    resetFormState();
    const tenantInfo = tokenStorage.getTenantInfo();
    setShowModal(true);
    requestAnimationFrame(() => {
      formInstanceRef.current?.setFieldsValue({
        username: '',
        nickname: '',
        password: '',
        phone: '',
        email: '',
        tenantId: tenantInfo?.id != null ? String(tenantInfo.id) : '',
      });
    });
  };

  const openEdit = (account: Account) => {
    setEditingAccount(account);
    setAvatarValue(account.avatar ?? '');
    setShowModal(true);
    requestAnimationFrame(() => {
      formInstanceRef.current?.setFieldsValue({
        username: account.username,
        nickname: account.nickname,
        password: '',
        phone: account.phone,
        email: account.email,
        tenantId: account.tenantId,
      });
    });
  };

  const openDetail = (account: Account) => {
    setDetailAccount(account);
    setShowDetail(true);
  };

  const handleFormSubmit = async () => {
    try {
      const result = await formInstanceRef.current?.validate();
      if (result !== true) {
        return;
      }
      const values = formInstanceRef.current?.getFieldsValue(true) || {};
      const tenantInfo = tokenStorage.getTenantInfo();
      const tenantId =
        tenantInfo?.id != null ? String(tenantInfo.id) : String(values.tenantId ?? '').trim();
      const payload = {
        username: String(values.username ?? '').trim(),
        nickname: String(values.nickname ?? '').trim(),
        email: String(values.email ?? '').trim(),
        phone: String(values.phone ?? '').trim(),
        tenantId,
        password: String(values.password ?? '').trim() || (editingAccount?.password ?? ''),
        avatar: avatarValue,
      };

      if (editingAccount) {
        setAccounts((list) =>
          list.map((item) =>
            item.id === editingAccount.id ? { ...item, ...payload } : item,
          ),
        );
        MessagePlugin.success('更新成功');
      } else {
        const maxId = Math.max(0, ...accounts.map((a) => a.id));
        const newAccount: Account = {
          id: maxId + 1,
          ...payload,
          avatar:
            payload.avatar ||
            `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(payload.nickname)}`,
        };
        setAccounts((list) => [...list, newAccount]);
        MessagePlugin.success('新增成功');
      }

      setShowModal(false);
      resetFormState();
    } catch {
      MessagePlugin.error('提交失败，请稍后重试');
    }
  };

  const handleDelete = (id: number) => {
    setAccounts((list) => list.filter((a) => a.id !== id));
    MessagePlugin.success('删除成功');
  };

  const columns = [
    {
      colKey: 'accountInfo',
      title: '账号信息',
      width: 240,
      cell: ({ row }: { row: Account }) => (
        <div className="flex items-center gap-3">
          <img
            src={row.avatar || AVATAR_FALLBACK}
            alt={row.nickname}
            className="w-10 h-10 rounded-full object-cover bg-gray-100"
            onError={(e) => {
              (e.target as HTMLImageElement).src = AVATAR_FALLBACK;
            }}
          />
          <div>
            <div className="text-sm font-medium text-gray-900">{row.nickname}</div>
            <div className="text-xs text-gray-400">{row.username}</div>
          </div>
        </div>
      ),
    },
    {
      colKey: 'phone',
      title: '手机号',
      width: 140,
      cell: ({ row }: { row: Account }) => (
        <div className="flex items-center gap-1 text-sm text-gray-700">
          <Phone className="w-3 h-3 text-gray-400" />
          {row.phone}
        </div>
      ),
    },
    {
      colKey: 'email',
      title: '邮箱',
      width: 200,
      cell: ({ row }: { row: Account }) => (
        <div className="flex items-center gap-1 text-sm text-gray-700 truncate max-w-[180px]">
          <Mail className="w-3 h-3 text-gray-400 shrink-0" />
          <span className="truncate">{row.email}</span>
        </div>
      ),
    },
    {
      colKey: 'tenantId',
      title: '租户 ID',
      width: 120,
      cell: ({ row }: { row: Account }) => (
        <div className="flex items-center gap-1 text-sm text-gray-700">
          <Building2 className="w-3 h-3 text-gray-400" />
          {row.tenantId}
        </div>
      ),
    },
    {
      colKey: 'password',
      title: '密码',
      width: 100,
      cell: ({ row }: { row: Account }) => (
        <span className="text-sm text-gray-500">{maskPassword(row.password)}</span>
      ),
    },
    {
      colKey: 'action',
      title: '操作',
      width: 220,
      fixed: 'right' as const,
      cell: ({ row }: { row: Account }) => (
        <Space>
          <Button
            theme="primary"
            variant="text"
            size="small"
            icon={<Eye className="w-4 h-4" />}
            onClick={() => openDetail(row)}
          >
            查看
          </Button>
          <Button
            theme="primary"
            variant="text"
            size="small"
            icon={<Pencil className="w-4 h-4" />}
            onClick={() => openEdit(row)}
          >
            编辑
          </Button>
          <Popconfirm content="确定删除该账号吗？" onConfirm={() => handleDelete(row.id)}>
            <Button theme="danger" variant="text" size="small" icon={<Trash2 className="w-4 h-4" />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <UserCircle className="w-6 h-6 text-[#1c5bff]" />
          账号管理
        </h1>
        <Button theme="primary" icon={<Plus className="w-4 h-4" />} onClick={openCreate}>
          新增账号
        </Button>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        当前为本地演示数据，接口接入后可直接替换为服务端增删改查。
      </p>

      <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm text-gray-600 mb-1">搜索</label>
            <Input
              value={searchText}
              onChange={setSearchText}
              placeholder="用户名 / 昵称 / 邮箱 / 手机号 / 租户 ID"
              clearable
              onEnter={handleSearch}
            />
          </div>
          <div className="flex gap-2">
            <Button theme="primary" icon={<Search className="w-4 h-4" />} onClick={handleSearch}>
              搜索
            </Button>
            <Button icon={<X className="w-4 h-4" />} onClick={handleReset}>
              重置
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <Table
          columns={columns as any}
          data={pagedData}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: filteredAccounts.length,
            showJumper: true,
            showPageSize: true,
            pageSizeOptions: [10, 20, 50],
            onChange: (pageInfo: { current?: number; pageSize?: number }) => {
              setPagination((prev) => ({
                ...prev,
                current: pageInfo.current ?? prev.current,
                pageSize: pageInfo.pageSize ?? prev.pageSize,
              }));
            },
          }}
          bordered={false}
          hover
          table-layout="fixed"
        />
      </div>

      <Dialog
        header={editingAccount ? '编辑账号' : '新增账号'}
        visible={showModal}
        onClose={() => {
          setShowModal(false);
          resetFormState();
        }}
        onConfirm={handleFormSubmit}
        confirmBtn="保存"
        width={520}
        key={editingAccount ? `edit-${editingAccount.id}` : 'add-new'}
      >
        <Form ref={formInstanceRef} labelWidth={100} initialData={editingAccount || undefined}>
        <FormItem label="头像">
              <ProductImageUploader
                simple
                value={avatarValue}
                onChange={setAvatarValue}
              />
            </FormItem>
            <FormItem
              label="用户名"
              name="username"
              rules={[{ required: true, message: '请输入用户名' }]}
            >
              <Input placeholder="登录用户名" />
            </FormItem>
            <FormItem
              label="昵称"
              name="nickname"
              rules={[{ required: true, message: '请输入昵称' }]}
            >
              <Input placeholder="显示昵称" />
            </FormItem>
            {!editingAccount ? (
              <FormItem
                label="密码"
                name="password"
                rules={[{ required: true, message: '请输入密码' }]}
              >
                <Input type="password" placeholder="登录密码" />
              </FormItem>
            ) : (
              <FormItem label="密码" name="password">
                <Input type="password" placeholder="留空则不修改" />
              </FormItem>
            )}
            <FormItem
              label="手机号"
              name="phone"
              rules={[
                { required: true, message: '请输入手机号' },
                { pattern: PHONE_PATTERN, message: '手机号格式不正确' },
              ]}
            >
              <Input placeholder="请输入11位手机号" />
            </FormItem>
            <FormItem
              label="邮箱"
              name="email"
              rules={[
                { required: true, message: '请输入邮箱' },
                { pattern: EMAIL_PATTERN, message: '邮箱格式不正确' },
              ]}
            >
              <Input placeholder="example@company.com" />
            </FormItem>
        </Form>
      </Dialog>

      <Dialog
        header="账号详情"
        visible={showDetail}
        onClose={() => {
          setShowDetail(false);
          setDetailAccount(null);
        }}
        footer={false}
        width={480}
      >
        {detailAccount && (
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-4">
              <img
                src={detailAccount.avatar || AVATAR_FALLBACK}
                alt={detailAccount.nickname}
                className="w-16 h-16 rounded-full object-cover bg-gray-100"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = AVATAR_FALLBACK;
                }}
              />
              <div>
                <div className="text-lg font-semibold text-gray-900">{detailAccount.nickname}</div>
                <div className="text-sm text-gray-500">{detailAccount.username}</div>
              </div>
            </div>
            <dl className="grid grid-cols-[100px_1fr] gap-y-3 text-sm">
              <dt className="text-gray-500">手机号</dt>
              <dd className="text-gray-900">{detailAccount.phone}</dd>
              <dt className="text-gray-500">邮箱</dt>
              <dd className="text-gray-900 break-all">{detailAccount.email}</dd>
              <dt className="text-gray-500">租户 ID</dt>
              <dd className="text-gray-900">{detailAccount.tenantId}</dd>
              <dt className="text-gray-500">密码</dt>
              <dd className="text-gray-900">{maskPassword(detailAccount.password)}</dd>
            </dl>
          </div>
        )}
      </Dialog>
    </div>
  );
}
